import { Layout } from 'antd';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import ParcelFloat from 'views/parcel-order/parcel-float';
import Footer from '../components/footer';
import Loading from '../components/loading';
import Sidebar from '../components/sidebar';
import SubscriptionsDate from '../components/subscriptions-date';
import TabMenu from '../components/tab-menu';
import { data } from '../configs/menu-config';
import { fetchAllShops } from '../redux/slices/allShops';
import { setUserData } from '../redux/slices/auth';
import { fetchCurrencies, fetchRestCurrencies } from '../redux/slices/currency';
import { setDefaultLanguage, setLangugages } from '../redux/slices/formLang';
import { fetchMyShop } from '../redux/slices/myShop';
import languagesService from '../services/languages';
import { sanitizeUserUrls } from '../helpers/role-access';
const { Content } = Layout;

const AppLayout = () => {
  const dispatch = useDispatch();
  const { languages } = useSelector((state) => state.formLang, shallowEqual);
  const { user } = useSelector((state) => state.auth, shallowEqual);
  const { direction, navCollapsed } = useSelector(
    (state) => state.theme.theme,
    shallowEqual,
  );
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 992);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchLanguages = useCallback(() => {
    languagesService.getAllActive().then((response) => {
      const data = response?.data || [];
      batch(() => {
        dispatch(setLangugages(data));
        dispatch(
          setDefaultLanguage(
            data?.find((item) => item?.default)?.locale || 'en',
          ),
        );
      });
    });
  }, [dispatch]);

  useEffect(() => {
    const body = {
      page: 1,
      perPage: 1,
      status: 'approved',
    };
    if (!languages.length) {
      fetchLanguages();
    }
    if (user?.role === 'seller' || user?.role === 'moderator') {
      dispatch(fetchMyShop());
    }
    if (user?.role === 'admin' || user?.role === 'manager') {
      dispatch(fetchAllShops(body));
      dispatch(fetchCurrencies());
    } else {
      dispatch(fetchRestCurrencies());
    }
  }, [dispatch, fetchLanguages, languages.length, user?.role]);

  useEffect(() => {
    if (!user || !user.role || !data[user.role]) return;

    // Always sync sidebar from menu-config so new items (e.g. lotteries)
    // appear without requiring logout. Persisted user.urls can be stale.
    const nextUrls = sanitizeUserUrls(user.role, data[user.role]);

    const hasSameUrls =
      Array.isArray(user.urls) &&
      JSON.stringify(user.urls) === JSON.stringify(nextUrls);

    if (hasSameUrls) return;

    const userObj = {
      ...user,
      urls: nextUrls,
    };
    dispatch(setUserData(userObj));
  }, [dispatch, user]);

  const getLayoutGutter = () => {
    // return navCollapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH
    if (isMobile) {
      return 0;
    }
    return navCollapsed ? 80 : 250;
  };

  const getLayoutDirectionGutter = () => {
    if (direction === 'ltr') {
      return { paddingLeft: getLayoutGutter(), minHeight: '100vh' };
    }
    if (direction === 'rtl') {
      return { paddingRight: getLayoutGutter(), minHeight: '100vh' };
    }
    return { paddingLeft: getLayoutGutter() };
  };

  return (
    <Layout className='app-container'>
      <Sidebar />
      <Layout className='app-layout' style={getLayoutDirectionGutter()}>
        <TabMenu />
        <Content className='p-3' style={{ flex: '1 0 70%' }}>
          <Suspense fallback={<Loading />}>
            <SubscriptionsDate />
            <Outlet />
          </Suspense>
        </Content>
        <Footer />
      </Layout>
      {user?.role === 'admin' && <ParcelFloat />}
    </Layout>
  );
};

export default AppLayout;
